import { QUERY_KEYS } from "@/common/query-keys";
import { useSettings } from "@/context/settings-context";
import { fetchAllModels } from "@/lib/snipeit/lookups";
import type { SnipeItModel } from "@/lib/snipeit/types";
import { useQuery } from "@tanstack/react-query";

export interface LowStockModel extends SnipeItModel {
  /** How far below the minimum: min_amt - remaining (positive = below minimum) */
  deficit: number;
}

/** Pure filter/sort logic – extracted for testability. */
export function filterLowStockModels(models: SnipeItModel[], blacklistIds: Set<number>): LowStockModel[] {
  return models
    .filter((m) => (m.min_amt ?? 0) > 0 && (m.remaining ?? 0) <= (m.min_amt ?? 0) + 5 && !blacklistIds.has(m.id))
    .map((m) => ({ ...m, deficit: (m.min_amt ?? 0) - (m.remaining ?? 0) }))
    .sort((a, b) => b.deficit / (b.min_amt ?? 1) - a.deficit / (a.min_amt ?? 1));
}

/**
 * Fetches all models whose available (`remaining`) count is at or below `min_amt + 5`,
 * sorted by deficit descending (most critical first).
 */
export function useLowStockModels() {
  const { deviceBlacklist } = useSettings();
  const blacklistSet = new Set(deviceBlacklist.map((b) => b.id));

  const query = useQuery({
    queryKey: [QUERY_KEYS.modelsLowStock],
    queryFn: fetchAllModels,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const items = filterLowStockModels(query.data ?? [], blacklistSet);

  return { items, isLoading: query.isLoading };
}
